import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Users, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import WhatsAppConfirmDialog from './WhatsAppConfirmDialog';
import { getWhatsAppLink } from './WhatsAppButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HireTalent = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    mobile: '',
    hiringRequirement: '',
    positions: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [whatsAppLink, setWhatsAppLink] = useState('');

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const link = getWhatsAppLink('hire-talent', formData);
      const response = await axios.post(`${BACKEND_URL}/api/hire-talent`, formData);
      if (response.data.success) {
        toast.success('Thank you! Our team will contact you soon.');
        setWhatsAppLink(link);
        setShowWhatsApp(true);
        setFormData({
          companyName: '',
          contactPerson: '',
          email: '',
          mobile: '',
          hiringRequirement: '',
          positions: '',
        });
        setIsOpen(false);
      }
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <WhatsAppConfirmDialog
      isOpen={showWhatsApp}
      onClose={setShowWhatsApp}
      whatsAppLink={whatsAppLink}
      formType="hire-talent"
    />
    <section id="hire-talent" className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #111827 0%, #0B0F1A 100%)'
    }}>
      {/* Neon glow orbs */}
      <div className="absolute top-20 left-20 w-96 h-96 bg-[#00E5FF] rounded-full blur-[150px] opacity-10 animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-80 h-80 bg-[#1DE9B6] rounded-full blur-[150px] opacity-10 animate-pulse" style={{ animationDelay: '2s' }}></div>

      <div className="max-w-7xl mx-auto text-center relative z-10">
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] mb-6 shadow-xl shadow-[#00E5FF]/50">
            <Users className="w-10 h-10 text-[#0B0F1A]" />
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 font-['Space_Grotesk'] text-white">Hire Skilled Talent from CloudXAI</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#00E5FF] to-transparent mx-auto mb-4"></div>
          <p className="text-xl text-[#38BDF8] max-w-3xl mx-auto">
            Trained, screened & industry-ready candidates for your organization
          </p>
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-6 mt-10">
          <Button
            onClick={() => setIsOpen(true)}
            className="bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold px-8 py-6 text-lg rounded-lg shadow-xl shadow-[#00E5FF]/50 hover:shadow-[#00E5FF]/70 transition-all duration-300 group"
          >
            <Briefcase className="mr-2 w-5 h-5 group-hover:scale-110 transition-transform" />
            Submit Hiring Requirement
          </Button>
          <Button
            className="border-2 border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 px-8 py-6 text-lg rounded-lg transition-all duration-300 bg-transparent"
            onClick={() => {
              const element = document.getElementById('footer');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Contact Us
          </Button>
        </div>

        {/* Hiring Form Dialog */}
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#111827] border-2 border-[#00E5FF]/30">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-white font-['Space_Grotesk']">Submit Hiring Requirement</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div>
                <Label htmlFor="companyName" className="text-[#F9FAFB]">Company Name *</Label>
                <Input
                  id="companyName"
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleChange('companyName', e.target.value)}
                  required
                  className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF]"
                  placeholder="Your company name"
                />
              </div>

              <div>
                <Label htmlFor="contactPerson" className="text-[#F9FAFB]">Contact Person *</Label>
                <Input
                  id="contactPerson"
                  type="text"
                  value={formData.contactPerson}
                  onChange={(e) => handleChange('contactPerson', e.target.value)}
                  required
                  className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF]"
                  placeholder="Your name"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="text-[#F9FAFB]">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF]"
                    placeholder="hr@company.com"
                  />
                </div>

                <div>
                  <Label htmlFor="mobile" className="text-[#F9FAFB]">Mobile Number *</Label>
                  <Input
                    id="mobile"
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => handleChange('mobile', e.target.value)}
                    required
                    className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF]"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="hiringRequirement" className="text-[#F9FAFB]">Hiring Requirement *</Label>
                <Textarea
                  id="hiringRequirement"
                  value={formData.hiringRequirement}
                  onChange={(e) => handleChange('hiringRequirement', e.target.value)}
                  required
                  className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF]"
                  rows={4}
                  placeholder="Describe the roles, skills, and qualifications needed..."
                />
              </div>

              <div>
                <Label htmlFor="positions" className="text-[#F9FAFB]">Number of Positions *</Label>
                <Input
                  id="positions"
                  type="text"
                  value={formData.positions}
                  onChange={(e) => handleChange('positions', e.target.value)}
                  required
                  className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF]"
                  placeholder="e.g., 5-10 positions"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold py-6 text-lg rounded-lg shadow-lg shadow-[#00E5FF]/40"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Requirement'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </section>
    </>
  );
};

export default HireTalent;
